import React from 'react'
import {createBrowserRouter, RouterProvider, redirect} from 'react-router-dom'
import About from './pages/about'
import Alarms from './pages/alarms'
import Alarm, {fetchAlarm} from './pages/alarms/alarm'
import ChangePassword, {setUser} from './pages/user/changePassword'
import CreateAlarm from './pages/alarms/create'
import CreateEvaluation from './pages/evaluations/create'
import CreateModel from './pages/models/create'
import Evaluation, {fetchEvaluation} from './pages/evaluations/evaluation'
import Evaluations from './pages/evaluations'
import EvaluationConnections from './pages/evaluations/connections'
import EvaluationSession, {
  fetchEvaluationSession,
} from './pages/evaluationSessions/evaluationSession'
import EvaluationTasks from './pages/evaluations/tasks'
import ErrorPage from './pages/error'
import Home from './pages/home'
import Login from './pages/user/login'
import LoginChecker from './pages/user/check'
import Model, {fetchModel} from './pages/models/model'
import Models from './pages/models'
import ModelRuns from './pages/models/runs'
import ModelConnections from './pages/models/connections'
import Profile, {fetchUser, fetchMe} from './pages/user/profile'
import ResetPassword from './pages/user/resetPassword'
import Register from './pages/user/register'
import Runs from './pages/evaluations/runs'
import Subscribe from './pages/subscribe'
import Test, {randomEvaluation} from './pages/test'
import useUser from './hooks/useUser'
import {Permission, CREATE_MODEL, CONNECT_MODELS, CREATE_EVALUATION, VIEW_RUNS} from './permissions'
import Path from './routeLinks'

type ItemGetter = (id: string) => Promise<any>
type Loader = (a: any) => any

const logoutAction = (logout: () => void) => () => {
  logout()
  // FIXME: This is a very ugly hack to get the page to refresh when logging out
  // from the home page, as it then doesn't do any redirects (what with it already
  // being on that page). Hopefully will get done properly along with proper logging in?
  window.location.href = Path.home()
  return null
}

const requiresUser = (loggedIn: boolean, loader?: Loader) => (args: any) => {
  if (!loggedIn) return redirect(Path.user.login(args?.request?.url))
  if (loader) return loader(args)
  return args
}

const getById = (getter: ItemGetter) => async (context: any) => ({
  ...context,
  item: getter(context.params.id).catch((error) => ({error})),
})

const itemFetcher = (path: string, getter: ItemGetter, element: React.ReactNode) => ({
  path,
  element,
  loader: getById(getter),
  errorElement: <ErrorPage />,
})

const requiresPermission =
  (hasPermission: (p: Permission) => boolean, user: any) =>
  (permission: Permission, loader?: Loader) =>
  (args: any) => {
    if (user !== undefined && !hasPermission(permission)) {
      return redirect(Path.subscriptions.all(args?.request?.url))
    }

    if (loader) return loader(args)
    return args
  }

const Routes = () => {
  const {logout, loggedIn, user, hasPermission, getUser} = useUser()

  const authPath = (path: string, element: any) => ({
    path,
    element,
    loader: requiresUser(loggedIn),
  })

  const permissionChecker = requiresPermission(hasPermission, user)
  const withPermission = (permission: Permission, route: any) => {
    const loader = requiresUser(loggedIn, permissionChecker(permission, route.loader))
    return {...route, loader}
  }

  const router = createBrowserRouter([
    {path: Path.home(), index: true, element: <Home />},
    {path: Path.about(), element: <About />},

    // Subscriptions
    authPath(Path.subscriptions.all(), <Subscribe />),

    // Evaluations
    withPermission(CREATE_EVALUATION, {
      path: Path.evaluations.create(),
      element: <CreateEvaluation />,
    }),
    {path: Path.tests.random(), loader: randomEvaluation},
    {
      path: Path.evaluations.tests(':id'),
      element: <Test />,
      loader: getById(fetchEvaluation),
      errorElement: <ErrorPage />,
    },
    withPermission(CONNECT_MODELS, {
      path: Path.evaluations.connections(':id'),
      element: <EvaluationConnections />,
      loader: requiresUser(loggedIn, getById(fetchEvaluation)),
      errorElement: <ErrorPage />,
    }),
    withPermission(VIEW_RUNS, {
      path: Path.evaluations.runs(':id'),
      element: <Runs />,
      loader: getById(fetchEvaluation),
      errorElement: <ErrorPage />,
    }),
    itemFetcher(Path.evaluations.show(':id'), fetchEvaluation, <Evaluation />),
    itemFetcher(Path.evaluations.tasks(':id'), fetchEvaluation, <EvaluationTasks />),
    {path: Path.evaluations.all(), element: <Evaluations />},

    // Evaluation Sessions
    itemFetcher(Path.evaluationSessions.show(':id'), fetchEvaluationSession, <EvaluationSession />),

    // Models
    {path: Path.models.all(), element: <Models />},
    withPermission(CREATE_MODEL, {
      path: Path.models.create(),
      element: <CreateModel />,
    }),
    withPermission(CONNECT_MODELS, {
      path: Path.models.connections(':id'),
      element: <ModelConnections />,
      loader: requiresUser(loggedIn, getById(fetchModel)),
      errorElement: <ErrorPage />,
    }),
    withPermission(VIEW_RUNS, {
      path: Path.models.runs(':id'),
      element: <ModelRuns />,
      loader: getById(fetchModel),
      errorElement: <ErrorPage />,
    }),
    itemFetcher(Path.models.show(':id'), fetchModel, <Model />),

    // Alarms
    {path: Path.alarms.all(), element: <Alarms />},
    authPath(Path.alarms.create(), <CreateAlarm />),
    itemFetcher(Path.alarms.show(':id'), fetchAlarm, <Alarm />),

    // Users
    {path: Path.user.forgotPassword(), element: <ResetPassword />},
    {path: Path.user.resetPassword(), loader: setUser(getUser), element: <ChangePassword />},
    {path: Path.user.logout(), loader: logoutAction(logout)},
    itemFetcher(Path.user.me(), fetchMe, <Profile />),
    itemFetcher(Path.user.profile(':id'), fetchUser, <Profile />),
    {path: Path.user.register(), element: <Register />},

    // Login
    {path: Path.login.github(), element: <LoginChecker />},
    {path: Path.user.login(), element: <Login />},

    {
      path: '*',
      loader: async () => {
        throw new Response('Not Found', {status: 404})
      },
      errorElement: <ErrorPage />,
    },
  ])

  return <RouterProvider router={router} />
}

export default Routes
